import {FormEvent, Fragment, useEffect, useState} from "react";
import {Badge, Button, Form, Table} from "react-bootstrap";
import {useSearchParams} from "react-router";
import {AuditApi} from "../../api/AuditApi";
import {
    AuditCategories,
    AuditCategory,
    AuditCategoryNames,
    AuditEntryViewModel,
    AuditFilter,
    AuditOutcome,
    AuditOutcomes
} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {formatDateTime} from "../../components/format";
import {usePersistentState} from "../../components/usePersistentState";

// What the filter form holds: dates as the date inputs have them (yyyy-mm-dd, in the viewer's time zone).
interface Filters {
    username: string;
    ip: string;
    category: '' | AuditCategory;
    outcome: '' | AuditOutcome;
    alerted: boolean;
    from: string;
    to: string;
}

const NO_FILTERS: Filters = { username: '', ip: '', category: '', outcome: '', alerted: false, from: '', to: '' };

const isFilters = (value: unknown): value is Filters => {
    const x = value as Filters;
    return !!x && typeof x === 'object' && typeof x.username === 'string' && typeof x.ip === 'string'
        && (x.category === '' || AuditCategories.includes(x.category)) && (x.outcome === '' || AuditOutcomes.includes(x.outcome))
        && typeof x.alerted === 'boolean' && typeof x.from === 'string' && typeof x.to === 'string';
};

const OUTCOME_BADGES: Record<AuditOutcome, { bg: string, text?: 'dark', label: string }> = {
    success: { bg: 'success', label: 'Success' },
    denied: { bg: 'warning', text: 'dark', label: 'Denied' },
    failed: { bg: 'secondary', label: 'Failed' },
    error: { bg: 'danger', label: 'Error' }
};

const ALERT_NAMES: { [rule: string]: string } = {
    'failed-signins-account': 'Failed sign-ins (account)',
    'failed-signins-ip': 'Failed sign-ins (address)',
    'admin-probing': 'Admin probing',
    'admin-new-location': 'Admin new location',
    'admin-burst': 'Admin burst',
    'server-errors': 'Server errors'
};

// A date input's day as the API's time: its start, or with endOfDay, the next day's start, in the viewer's time zone.
function dayToIso(day: string, endOfDay: boolean = false): string | undefined {
    if (!day) {
        return undefined;
    }
    const [year, month, date] = day.split('-').map(Number);
    return new Date(year, month - 1, date + (endOfDay ? 1 : 0)).toISOString();
}

// Only the filters that are set.
function toApiFilter(filters: Filters): AuditFilter {
    const all: AuditFilter = {
        username: filters.username.trim() || undefined,
        ip: filters.ip.trim() || undefined,
        category: filters.category || undefined,
        outcome: filters.outcome || undefined,
        alerted: filters.alerted || undefined,
        from: dayToIso(filters.from),
        to: dayToIso(filters.to, true)
    };
    return Object.fromEntries(Object.entries(all).filter(([, value]) => value !== undefined));
}

// The audit log's links (in the alert emails): ?username=, ?ip=, ?outcome= and ?alerted=true. They replace the
// remembered filters.
function filtersFromLink(params: URLSearchParams): Filters | undefined {
    if (!['username', 'ip', 'outcome', 'alerted'].some(x => params.has(x))) {
        return undefined;
    }
    const outcome = params.get('outcome') as AuditOutcome;
    return {
        ...NO_FILTERS,
        username: params.get('username') ?? '',
        ip: params.get('ip') ?? '',
        outcome: AuditOutcomes.includes(outcome) ? outcome : '',
        alerted: params.get('alerted') === 'true'
    };
}

// Who made the request: the signed-in user, or for a sign-in, the account it was for.
function Who({entry, onFilter}: { entry: AuditEntryViewModel, onFilter(username: string): void }) {
    const username = entry.actor.username || entry.actor.attemptedUsername;
    if (!username) {
        return <span className="text-body-secondary">anonymous</span>;
    }
    return (
        <>
            <Button variant="link" className="p-0 align-baseline" title="Show only this user"
                    onClick={e => { e.stopPropagation(); onFilter(username); }}>{username}</Button>
            {entry.actor.isAdmin && <Badge bg="primary" className="ms-1">Admin</Badge>}
            {!entry.actor.username && <span className="text-body-secondary small ms-1">(sign-in)</span>}
        </>
    );
}

// The facts the API added (a sign-in's reason, a license's transfer), as "name: value".
function detailsText(entry: AuditEntryViewModel): string {
    return Object.entries(entry.details).map(([name, value]) => `${name}: ${value}`).join(', ');
}

function EntryDetails({entry}: { entry: AuditEntryViewModel }) {
    const rows: [string, string][] = [
        ['Request id', entry.requestId],
        ['Status', String(entry.statusCode)],
        ['Category', AuditCategoryNames[entry.category] ?? entry.category],
        ['Session', entry.actor.deviceId],
        ['Claims', entry.actor.claims.map(x => x.substring(x.lastIndexOf('/') + 1)).join(', ')],
        ['X-Forwarded-For', entry.forwardedFor],
        ['Origin', entry.origin],
        ['User agent', entry.userAgent]
    ];
    return (
        <dl className="row mb-0 small">
            {rows.filter(([, value]) => value).map(([name, value]) => (
                <Fragment key={name}>
                    <dt className="col-sm-2">{name}</dt>
                    <dd className="col-sm-10 text-break">{value}</dd>
                </Fragment>
            ))}
            {Object.keys(entry.details).length > 0 && (
                <>
                    <dt className="col-sm-2">Details</dt>
                    <dd className="col-sm-10"><pre className="mb-0">{JSON.stringify(entry.details, null, 2)}</pre></dd>
                </>
            )}
            {entry.body !== undefined && (
                <>
                    <dt className="col-sm-2">Body (redacted)</dt>
                    <dd className="col-sm-10"><pre className="mb-0">{JSON.stringify(entry.body, null, 2)}</pre></dd>
                </>
            )}
        </dl>
    );
}

// Everything the API's audit trail kept, newest first, filtered on the server: who did what, from where, and how it
// went. Usernames and addresses are links that narrow the list to them.
export function AuditLogPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filters, setFilters] = usePersistentState<Filters>('admin-audit:filters', NO_FILTERS, isFilters);
    const linked = filtersFromLink(searchParams);
    const link = searchParams.toString();
    // The text fields apply on Enter (or Apply), not on each keystroke.
    const [draft, setDraft] = useState({ username: filters.username, ip: filters.ip });
    const [expanded, setExpanded] = useState<string>();
    const [moreError, setMoreError] = useState<unknown>();
    const [loadingMore, setLoadingMore] = useState(false);

    // Once: the link's filters become the remembered ones, and leave the address.
    useEffect(() => {
        const fromLink = filtersFromLink(new URLSearchParams(link));
        if (fromLink) {
            setFilters(fromLink);
            setDraft({ username: fromLink.username, ip: fromLink.ip });
            setSearchParams({}, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link]);

    const current = linked ?? filters;
    const {data: page, error, loading, setData} = useAsync(() => AuditApi.query(toApiFilter(current)), [JSON.stringify(current)]);

    const update = (changes: Partial<Filters>) => {
        setFilters(x => ({ ...x, ...changes }));
        setMoreError(undefined);
    };
    const applyText = (e: FormEvent) => {
        e.preventDefault();
        update(draft);
    };
    const pivot = (changes: { username?: string, ip?: string }) => {
        setDraft(x => ({ ...x, ...changes }));
        update(changes);
    };
    const clear = () => {
        setDraft({ username: '', ip: '' });
        update(NO_FILTERS);
    };
    const loadMore = async () => {
        if (!page?.nextBefore) {
            return;
        }
        setLoadingMore(true);
        setMoreError(undefined);
        try {
            const next = await AuditApi.query(toApiFilter(current), page.nextBefore);
            setData(x => x && { entries: [...x.entries, ...next.entries], nextBefore: next.nextBefore });
        } catch (err) {
            setMoreError(err);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <>
            <PageHeader title="Audit log"/>
            <p className="text-body-secondary">
                Every change, every administrator's request (but for reading this log), sign-ins, and refused or failed requests, kept for two years.
                Security alerts are emailed as they happen.
            </p>
            <Form onSubmit={applyText} className="d-flex flex-wrap align-items-end gap-2 mb-3">
                <Form.Group controlId="audit-username">
                    <Form.Label className="small mb-1">User</Form.Label>
                    <Form.Control size="sm" value={draft.username} onChange={e => setDraft(x => ({ ...x, username: e.target.value }))} onBlur={() => update(draft)}/>
                </Form.Group>
                <Form.Group controlId="audit-ip">
                    <Form.Label className="small mb-1">IP address</Form.Label>
                    <Form.Control size="sm" value={draft.ip} onChange={e => setDraft(x => ({ ...x, ip: e.target.value }))} onBlur={() => update(draft)}/>
                </Form.Group>
                <Form.Group controlId="audit-category">
                    <Form.Label className="small mb-1">Area</Form.Label>
                    <Form.Select size="sm" value={current.category} onChange={e => update({ category: e.target.value as Filters['category'] })}>
                        <option value="">All areas</option>
                        {AuditCategories.map(x => <option key={x} value={x}>{AuditCategoryNames[x]}</option>)}
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="audit-outcome">
                    <Form.Label className="small mb-1">Outcome</Form.Label>
                    <Form.Select size="sm" value={current.outcome} onChange={e => update({ outcome: e.target.value as Filters['outcome'] })}>
                        <option value="">All outcomes</option>
                        {AuditOutcomes.map(x => <option key={x} value={x}>{OUTCOME_BADGES[x].label}</option>)}
                    </Form.Select>
                </Form.Group>
                <Form.Group controlId="audit-from">
                    <Form.Label className="small mb-1">From</Form.Label>
                    <Form.Control size="sm" type="date" value={current.from} onChange={e => update({ from: e.target.value })}/>
                </Form.Group>
                <Form.Group controlId="audit-to">
                    <Form.Label className="small mb-1">To</Form.Label>
                    <Form.Control size="sm" type="date" value={current.to} onChange={e => update({ to: e.target.value })}/>
                </Form.Group>
                <Form.Check id="audit-alerted" className="mb-1" label="Alerts only" checked={current.alerted}
                            onChange={e => update({ alerted: e.target.checked })}/>
                <Button type="submit" size="sm" variant="primary">Apply</Button>
                <Button size="sm" variant="outline-secondary" onClick={clear}>Clear</Button>
            </Form>

            <ErrorAlert error={error}/>
            {loading && !page && <LoadingSpinner/>}
            {page && (
                <>
                    <Table responsive hover size="sm">
                        <thead>
                        <tr>
                            <th>Time</th>
                            <th>User</th>
                            <th>Action</th>
                            <th>Outcome</th>
                            <th>IP address</th>
                        </tr>
                        </thead>
                        <tbody>
                        {page.entries.map(entry => (
                            <Fragment key={entry.id}>
                                <tr className="clickable-row" onClick={() => setExpanded(x => x === entry.id ? undefined : entry.id)}
                                    aria-expanded={expanded === entry.id}>
                                    <td className="text-nowrap">{formatDateTime(entry.occurredOn)}</td>
                                    <td className="text-nowrap"><Who entry={entry} onFilter={username => pivot({ username })}/></td>
                                    <td>
                                        <code>{entry.action}</code>
                                        {Object.keys(entry.pathParameters).length > 0 && (
                                            <div className="small text-body-secondary">{Object.values(entry.pathParameters).join(', ')}</div>
                                        )}
                                        {Object.keys(entry.details).length > 0 && <div className="small">{detailsText(entry)}</div>}
                                    </td>
                                    <td>
                                        <Badge bg={OUTCOME_BADGES[entry.outcome].bg} text={OUTCOME_BADGES[entry.outcome].text}>
                                            {OUTCOME_BADGES[entry.outcome].label} ({entry.statusCode})
                                        </Badge>
                                        {entry.alerts.map(x => (
                                            <Badge key={x} bg="danger" className="ms-1" title="This request set off a security alert">
                                                <i className="bi bi-exclamation-triangle-fill me-1" aria-hidden="true"></i>{ALERT_NAMES[x] ?? x}
                                            </Badge>
                                        ))}
                                    </td>
                                    <td className="text-nowrap">
                                        <Button variant="link" className="p-0 align-baseline" title="Show only this address"
                                                onClick={e => { e.stopPropagation(); pivot({ ip: entry.ip }); }}>{entry.ip}</Button>
                                    </td>
                                </tr>
                                {expanded === entry.id && (
                                    <tr><td colSpan={5} className="bg-body-tertiary"><EntryDetails entry={entry}/></td></tr>
                                )}
                            </Fragment>
                        ))}
                        {page.entries.length === 0 && (
                            <tr><td colSpan={5} className="text-center text-body-secondary py-4">Nothing in the audit log matches the filter.</td></tr>
                        )}
                        </tbody>
                    </Table>
                    <ErrorAlert error={moreError}/>
                    {page.nextBefore && (
                        <Button variant="outline-primary" onClick={loadMore} disabled={loadingMore}>
                            {loadingMore ? 'Loading…' : 'Load more'}
                        </Button>
                    )}
                </>
            )}
        </>
    );
}
